import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleAlert, Plus } from "lucide-react";
import type { ReservationAssignedPlan } from "@/hooks/useReservationBillingOptions";
import type { StayPlanAlertKind } from "@/lib/daycare-hotel/stayPlans";

export interface StayCoverageChoiceProps {
  choice: "existing" | "avulso" | null;
  petId: string | null;
  isLoading: boolean;
  isError: boolean;
  assignedContracts: ReservationAssignedPlan[];
  selectedAssignedId: string | null;
  currentCoverage?: { kind: "plan" | "package" | "avulso"; contractId: string | null; name: string } | null;
  alerts: StayPlanAlertKind[];
  usageSummary: { used: number; limit: number; period: "week" | "cycle" } | null;
  reservationUnits: number;
  weeklyReservationUnits: number;
  onChange: (next: { choice: "existing" | "avulso" | null; assignedId: string | null }) => void;
  onContract: (kindFilter: "recurring" | "day_package") => void;
  disabled?: boolean;
}

const rowClass = "flex min-h-12 w-full items-center gap-3 px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60";
const linkClass = "inline-flex min-h-11 items-center gap-1 rounded-md text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60";

export function StayCoverageChoice({
  choice,
  petId,
  isLoading,
  isError,
  assignedContracts,
  selectedAssignedId,
  currentCoverage = null,
  alerts,
  usageSummary,
  reservationUnits,
  weeklyReservationUnits,
  onChange,
  onContract,
  disabled = false,
}: StayCoverageChoiceProps) {
  const { t } = useTranslation("daycareHotel");
  const [expanded, setExpanded] = useState(!currentCoverage);
  const currentCoverageKey = currentCoverage
    ? `${currentCoverage.kind}:${currentCoverage.contractId ?? ""}:${currentCoverage.name}`
    : null;

  useEffect(() => {
    setExpanded(currentCoverageKey === null);
  }, [currentCoverageKey]);

  const projectedUnits = usageSummary?.period === "week" ? weeklyReservationUnits : reservationUnits;
  const displayedUsage = usageSummary ? usageSummary.used + projectedUnits : 0;
  const isUsageExhausted = usageSummary ? displayedUsage >= usageSummary.limit : false;
  const isLastWeeklyReservation = usageSummary?.period === "week"
    && alerts.includes("last_week_unit")
    && displayedUsage === usageSummary.limit;
  const coverageKind = currentCoverage?.kind ?? (choice === "avulso" ? "avulso" : null);

  const renderUsage = () => {
    if (!usageSummary) return null;
    const text = usageSummary.period === "week"
      ? t("planChoice.weeklyUsage", { used: displayedUsage, limit: usageSummary.limit })
      : t("planChoice.cycleUsage", { used: displayedUsage, limit: usageSummary.limit });
    if (isUsageExhausted && !isLastWeeklyReservation) {
      return <span role="alert" className="flex items-center gap-1 text-sm text-destructive"><CircleAlert aria-hidden="true" className="size-4" />{text}</span>;
    }
    return <span className="text-sm text-muted-foreground">{text}</span>;
  };

  if (!expanded && currentCoverage) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">{t(`planChoice.kindBadge.${coverageKind ?? "avulso"}`)}</span>
            <span className="truncate text-sm font-semibold text-foreground">{currentCoverage.name || t("planChoice.payAsYouGoOption")}</span>
          </div>
          <div className="mt-1">{renderUsage()}</div>
        </div>
        <button type="button" disabled={disabled} onClick={() => setExpanded(true)} className="shrink-0 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t("planChoice.change")}</button>
      </div>
    );
  }

  const nameCounts = assignedContracts.reduce<Record<string, number>>((counts, contract) => {
    counts[contract.name] = (counts[contract.name] ?? 0) + 1;
    return counts;
  }, {});

  const renderRow = (key: string, checked: boolean, label: string, detail: string, onSelect: () => void) => (
    <button key={key} type="button" role="radio" aria-checked={checked} disabled={disabled} onClick={onSelect} className={`${rowClass} ${checked ? "bg-accent" : "bg-card hover:bg-muted"}`}>
      <span aria-hidden="true" className={`flex size-[18px] shrink-0 items-center justify-center rounded-full border-2 ${checked ? "border-primary" : "border-border"}`}>
        {checked ? <span className="size-2 rounded-full bg-primary" /> : null}
      </span>
      <span className={`min-w-0 flex-1 truncate text-sm text-foreground ${checked ? "font-semibold" : ""}`}>{label}</span>
      <span className="shrink-0 text-sm text-muted-foreground">{detail}</span>
    </button>
  );

  return (
    <div className="grid gap-3">
      <div role="radiogroup" aria-label={t("planChoice.label")} className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
        {assignedContracts.map((contract) => {
          const kind = contract.kind === "day_package" ? "package" : "plan";
          const label = `${t(`planChoice.kindBadge.${kind}`)} ${contract.name}`;
          const balance = contract.remainingDays === null ? t("planChoice.unlimitedPlan") : t("planChoice.availableDays", { count: contract.remainingDays });
          const detail = nameCounts[contract.name] > 1
            ? `${t("planChoice.startedOn", { date: new Date(contract.startsOn).toLocaleDateString(undefined, { timeZone: "UTC" }) })} · ${balance}`
            : balance;
          return renderRow(contract.contractId, selectedAssignedId === contract.contractId, label, detail, () => { onChange({ choice: "existing", assignedId: contract.contractId }); setExpanded(false); });
        })}
        {renderRow("avulso", choice === "avulso", t("planChoice.payAsYouGoOption"), t("planChoice.avulsoOptionDescription"), () => { onChange({ choice: "avulso", assignedId: null }); setExpanded(false); })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <button type="button" disabled={disabled || !petId} onClick={() => onContract("recurring")} className={linkClass}><Plus aria-hidden="true" className="size-4" />{t("planChoice.contractPlan")}</button>
        <button type="button" disabled={disabled || !petId} onClick={() => onContract("day_package")} className={linkClass}><Plus aria-hidden="true" className="size-4" />{t("planChoice.contractPackageShort")}</button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {isLoading ? <span className="text-sm text-muted-foreground">{t("planChoice.loadingPetPlans")}</span> : null}
        {isError ? <span role="alert" className="text-sm text-destructive">{t("planChoice.petPlansLoadError")}</span> : null}
        {alerts.filter((alert) => alert !== "overdue" && alert !== "due_soon").map((alert) => <span key={alert} className="rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground">{t(`planChoice.alerts.${alert}`)}</span>)}
        {renderUsage()}
      </div>
    </div>
  );
}
